import { Permissions } from '#app/config'
import { useFeatureFlags } from '#app/utils/pz/featureFlags'
import { useOptionalUser } from '#app/utils/user'

export type NavItemLike = {
  featureFlag?: string
  allowedRoles?: string[]
  deniedRoles?: string[]
  permissionKeys?: string[]
  requiresAuth?: boolean
}

function normFlagCandidates(ff: string): string[] {
  return [ff, ff.replace(/^admin\.preferences\.features\./, ''), ff.replace(/^features\./, '')]
}

function hasAnyRole(user: any | undefined, roles: string[]): boolean {
  if (!user) return false
  return roles.some((r) => user.roles?.some?.((ur: any) => ur.name === r))
}

function checkPermissionKeys(user: any | undefined, permissionKeys?: string[]): boolean {
  if (!permissionKeys || permissionKeys.length === 0) return true
  // permission key format: e.g. "course.actions.createCourse"
  for (const key of permissionKeys) {
    const parts = key.split('.')
    let current: any = Permissions as any
    let ok = true
    for (const p of parts) {
      if (!current || typeof current !== 'object' || !(p in current)) {
        ok = false
        break
      }
      current = current[p]
    }
    if (!ok) return false
    const allowed: string[] | undefined = current?.allowedRoles
    if (Array.isArray(allowed) && allowed.length > 0) {
      if (!hasAnyRole(user, allowed)) return false
    }
  }
  return true
}

export function canSeeNavItemWithContext(item: NavItemLike, ctx: { featureFlags: any; user: any }): boolean {
  const { featureFlags, user } = ctx
  if (item.requiresAuth && !user) return false

  if (item.featureFlag) {
    const candidates = normFlagCandidates(item.featureFlag)
    let pass = false
    for (const cand of candidates) {
      const parts = cand.split('.')
      let cur: any = featureFlags
      let ok = true
      for (const p of parts) {
        if (!cur || typeof cur !== 'object' || !(p in cur)) { ok = false; break }
        cur = cur[p]
      }
      if (ok && Boolean(cur)) { pass = true; break }
    }
    if (!pass) return false
  }

  if (item.allowedRoles && item.allowedRoles.length > 0) {
    if (!hasAnyRole(user, item.allowedRoles)) return false
  }
  if (item.deniedRoles && item.deniedRoles.length > 0) {
    if (hasAnyRole(user, item.deniedRoles)) return false
  }

  if (!checkPermissionKeys(user, item.permissionKeys)) return false
  return true
}

export function useCanSeeNavItem(item: NavItemLike): boolean {
  const featureFlags = useFeatureFlags()
  const user = useOptionalUser()
  return canSeeNavItemWithContext(item, { featureFlags, user })
}
